import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Plane, CalendarClock, Users, Gavel, MapPinned, MessageSquare,
  Moon, Sun, Car, ChevronRight, ShieldCheck, Star,
} from "lucide-react";
import Logo from "@/components/Logo";
import Button from "@/components/Button";
import HeroBooking from "@/components/HeroBooking";
import { useTheme } from "@/lib/theme";

const SUGGESTIONS = [
  { icon: Plane, title: "Airport rides", body: "Door-to-door to and from Orlando International (MCO)." },
  { icon: CalendarClock, title: "Reserve ahead", body: "Schedule your pickup up to 7 days in advance." },
  { icon: Users, title: "Groups & bags", body: "Economy, SUV and Executive options for every party size." },
];

const STEPS = [
  { icon: MapPinned, title: "Request your airport ride", body: "Set your pickup, drop-off, flight and party size." },
  { icon: Gavel, title: "Drivers bid for your trip", body: "Compare up to five real driver offers and prices." },
  { icon: MessageSquare, title: "Choose, track & chat", body: "Pick your driver, track live and message them anytime." },
];

export default function GuestLanding() {
  const navigate = useNavigate();
  const { dark, toggle } = useTheme();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-border bg-card/90 px-5 py-3 backdrop-blur-md">
        <button onClick={() => navigate("/")} data-testid="logo-home"><Logo size={32} /></button>
        <div className="flex items-center gap-2">
          <button onClick={toggle} data-testid="landing-theme-toggle" className="hidden h-10 w-10 items-center justify-center rounded-full bg-secondary text-foreground hover:bg-muted md:flex">
            {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
          <Button variant="ghost" size="sm" onClick={() => navigate("/auth")} data-testid="landing-login">Log in</Button>
          <Button size="sm" onClick={() => navigate("/auth")} data-testid="landing-signup">Sign up</Button>
        </div>
      </header>

      {/* Hero */}
      <HeroBooking guest />

      {/* Suggestions */}
      <section className="mx-auto max-w-6xl px-5 py-16">
        <h2 className="text-2xl font-extrabold text-foreground">Ways to ride with Getaride</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {SUGGESTIONS.map((s) => (
            <div key={s.title} className="group rounded-2xl border border-border bg-card p-6 transition-all hover:shadow-soft">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-brand-600 transition-transform group-hover:scale-105">
                <s.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-4 text-lg font-bold text-foreground">{s.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Log in to see activity */}
      <section className="mx-auto max-w-6xl px-5 pb-16">
        <div className="flex flex-col items-start justify-between gap-6 rounded-3xl bg-gradient-to-br from-brand-600 to-brand-700 p-8 text-white sm:flex-row sm:items-center">
          <div>
            <h2 className="text-2xl font-extrabold">Log in to see your recent activity</h2>
            <p className="mt-2 max-w-lg text-white/85">View past trips, tax info, receipts and manage your saved payment methods — all in one place.</p>
          </div>
          <div className="flex shrink-0 gap-3">
            <button onClick={() => navigate("/auth")} data-testid="activity-login" className="rounded-full bg-white px-6 py-3 text-sm font-bold text-brand-700 transition-transform hover:scale-[1.02]">Log in</button>
            <button onClick={() => navigate("/auth")} className="rounded-full border border-white/60 px-6 py-3 text-sm font-bold text-white hover:bg-white/10">Create account</button>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-6xl px-5 pb-16">
        <h2 className="text-2xl font-extrabold text-foreground">How Getaride works</h2>
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <div key={s.title} className="rounded-2xl border border-border bg-card p-6">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-600 font-num text-lg font-bold text-white">{i + 1}</span>
                <s.icon className="h-6 w-6 text-brand-600" />
              </div>
              <h3 className="mt-4 text-lg font-bold text-foreground">{s.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Drive with us */}
      <section className="mx-auto max-w-6xl px-5 pb-16">
        <div className="flex flex-col items-start justify-between gap-6 rounded-3xl border border-border bg-card p-8 sm:flex-row sm:items-center">
          <div className="flex items-start gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-accent text-brand-600"><Car className="h-7 w-7" /></span>
            <div>
              <h2 className="text-2xl font-extrabold text-foreground">Drive when you want, name your fare</h2>
              <p className="mt-2 max-w-lg text-muted-foreground">Turn your car into earnings with Orlando's airport-focused marketplace. Set your own prices and drive on your schedule.</p>
            </div>
          </div>
          <Button size="lg" className="shrink-0" onClick={() => navigate("/drive-with-us")} data-testid="landing-drive">
            Start driving <ChevronRight className="h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-card">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-5 py-8 sm:flex-row">
          <Logo size={28} />
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1"><ShieldCheck className="h-4 w-4 text-success" /> Verified drivers</span>
            <span className="flex items-center gap-1"><Star className="h-4 w-4 fill-amber-400 text-amber-400" /> Rated rides</span>
          </div>
          <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} Getaride Orlando</p>
        </div>
      </footer>
    </div>
  );
}
